import { Box, Flex, Icon, Text, VStack } from '@chakra-ui/react';
import { Dispatch, FunctionComponent, SetStateAction, useRef, useState } from 'react';
import { Upload } from '@marketcast/ui-kit';
import { FileList } from '@/components/FileList';
import { FILE_UPLOAD_STATUS } from '@/constants';
import { SelectedUploadFile } from '@/types/upload';

type Props = {
  allowedExtensions: string[];
  maxFiles: number;
  files: SelectedUploadFile[];
  onFilesChange: Dispatch<SetStateAction<SelectedUploadFile[]>>;
  showList?: boolean;
  isDisabled?: boolean;
};

const hasAllowedExtension = (fileName: string, allowedExtensions: string[]) =>
  allowedExtensions.some((extension) => fileName.toLowerCase().endsWith(extension));

const toSelectedFile = (file: File): SelectedUploadFile => ({ file, progress: 0, status: FILE_UPLOAD_STATUS.PENDING });

/** Client-side file selection only (pick, drop, validate, remove); uploading is up to the consumer. */
export const FileUpload: FunctionComponent<Props> = ({
  allowedExtensions,
  maxFiles,
  files,
  onFilesChange,
  showList = false,
  isDisabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPickerDisabled = isDisabled || files.length >= maxFiles;

  const addFiles = (incoming: File[]) => {
    setError(null);

    const unsupported = incoming.filter((file) => !hasAllowedExtension(file.name, allowedExtensions));
    if (unsupported.length > 0) {
      setError(
        `Unsupported file type: ${unsupported.map((file) => file.name).join(', ')}. ` +
          `Allowed: ${allowedExtensions.join(', ')}`
      );
      return;
    }

    // Same name maps to the same S3 key and the backend rejects duplicates, so drop
    // re-picks of a file that's already in the list.
    const existingNames = new Set(files.map((item) => item.file.name));
    const newFiles = incoming.filter((file) => !existingNames.has(file.name));

    if (files.length + newFiles.length > maxFiles) {
      setError(`You can upload up to ${maxFiles} file${maxFiles === 1 ? '' : 's'}.`);
      return;
    }

    // Functional update: the consumer may be writing upload progress into the same list.
    onFilesChange((previous) => [...previous, ...newFiles.map(toSelectedFile)]);
  };

  const removeFile = (fileName: string) => {
    setError(null);
    onFilesChange((previous) => previous.filter((item) => item.file.name !== fileName));
  };

  const openFilePicker = () => {
    if (!isPickerDisabled) {
      fileInputRef.current?.click();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    if (selected.length > 0) {
      addFiles(selected);
    }
    // Reset so picking the same file again (e.g. after removing it) still fires onChange.
    event.target.value = '';
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFilePicker();
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isPickerDisabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (isPickerDisabled) return;

    const dropped = Array.from(event.dataTransfer.files ?? []);
    if (dropped.length > 0) {
      addFiles(dropped);
    }
  };

  return (
    <Flex direction="column" gap={4} width="100%">
      <Box
        role="button"
        tabIndex={isPickerDisabled ? -1 : 0}
        aria-label="Upload files"
        aria-disabled={isPickerDisabled}
        border="2px dashed"
        borderColor={isDragging ? 'marketcast.primaryCta.1' : 'marketcast.gray.4'}
        bg={isDragging ? 'marketcast.primaryCta25.1' : 'marketcast.base.background'}
        borderRadius="12px"
        textAlign="center"
        cursor={isPickerDisabled ? 'not-allowed' : 'pointer'}
        opacity={isPickerDisabled ? 0.6 : 1}
        width="100%"
        height="160px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        transition="all 0.15s ease-in-out"
        _hover={
          isPickerDisabled
            ? undefined
            : {
                borderColor: 'marketcast.primaryCta.1',
                bg: 'marketcast.base.backgroundAlt',
              }
        }
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={openFilePicker}
        onKeyDown={handleKeyDown}
        p={8}
      >
        <VStack spacing={3}>
          <Icon as={Upload} boxSize="40px" color="marketcast.primaryCta.1" />
          <Text color="marketcast.text.primary" fontWeight="600" fontSize="18px">
            Drag &apos;n&apos; drop files or click to browse
          </Text>
          <Text color="marketcast.gray.2" fontSize="14px">
            Supported file types:{' '}
            {allowedExtensions.map((extension) => extension.toUpperCase()).join(', ')}
          </Text>
          <Text color="marketcast.gray.2" fontSize="14px">
            Up to {maxFiles} file{maxFiles === 1 ? '' : 's'}
          </Text>
        </VStack>
        <input
          ref={fileInputRef}
          type="file"
          multiple={maxFiles > 1}
          accept={allowedExtensions.join(',')}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          disabled={isPickerDisabled}
        />
      </Box>

      {error && (
        <Text fontSize="xs" color="red.500" role="alert">
          {error}
        </Text>
      )}

      {showList && <FileList files={files} onRemove={removeFile} isRemoveDisabled={isDisabled} />}
    </Flex>
  );
};
